import { type FunctionComponent } from 'react'
import { format } from 'date-fns'
import { Info } from 'lucide-react'
import { Avatar } from 'tailwind-ui'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'tailwind-v4/components/popover'
import { type InboxThreadType } from '@/state/entities/inbox/inbox.types'
import { gravatarImage } from '@/utils/common'
import { TimeFormats } from '@/common/datepicker/utilities/formats'

export const ChatAvatar: FunctionComponent<{
  email: string
  name: string
  className?: string
}> = ({ name, email, className = '' }) => (
  <Avatar
    name={name}
    src={gravatarImage(email)}
    alt={name}
    cssBg=""
    textClassName="text-white/90 dark:text-black/90"
    className={`size-8 bg-black/50 dark:bg-white/50 rounded-full ${className}`}
  />
)

const ContactList: FunctionComponent<{
  label: string
  contacts: any[]
}> = ({ label, contacts }) => {
  if (!contacts.length) return null
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
        {label}
      </p>
      {contacts.map((c: any) => (
        <p key={c.email} className="truncate text-sm">
          {c.name ? <span className="font-medium">{c.name} </span> : null}
          <span className="text-[var(--muted-foreground)]">{`<${c.email}>`}</span>
        </p>
      ))}
    </div>
  )
}

/**
 * Hover/click detail popover for a message — shows From/To/Cc/Subject/date for
 * emails, or author + date for internal messages. Self-contained: renders its
 * own info-icon trigger so message rows just drop in `<ThreadPopover event />`.
 */
export const ThreadPopover: FunctionComponent<{
  event: InboxThreadType['events']['data'][number]['eventable']
}> = ({ event }) => {
  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label="Message details"
            className="inline-flex size-5 items-center justify-center rounded-md text-[var(--muted-foreground)] hover:bg-black/5 hover:text-[var(--foreground)] dark:hover:bg-white/10"
          />
        }
      >
        <Info className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 text-sm">
        {'user_name' in event ? (
          <div className="space-y-0.5">
            <p className="font-medium">{event.user_name}</p>
            <p className="text-[var(--muted-foreground)]">
              {format(event.created_at, TimeFormats.StandardWithTime)}
            </p>
          </div>
        ) : null}

        {'thread_contact' in event ? (
          <div className="flex flex-col gap-0.5">
            <p className="font-medium">
              {event.thread_contact.name || event.thread_contact.email}
            </p>
            {event.thread_contact.email ? (
              <p className="text-[var(--muted-foreground)]">
                {event.thread_contact.email}
              </p>
            ) : null}
            <p className="text-[var(--muted-foreground)]">
              {format(event.created_at, TimeFormats.StandardWithTime)}
            </p>
          </div>
        ) : null}

        {'contacts' in event ? (
          <>
            <ContactList
              label="From"
              contacts={event.contacts.filter((c: any) => c.type === 'from')}
            />
            <ContactList
              label="To"
              contacts={event.contacts.filter((c: any) => c.type === 'to')}
            />
            <ContactList
              label="Cc"
              contacts={event.contacts.filter((c: any) => c.type === 'cc')}
            />
            <div className="space-y-0.5 border-t border-[var(--border)] pt-2">
              <p className="text-[var(--muted-foreground)]">
                {format(event.created_at, TimeFormats.StandardWithTime)}
              </p>
              {'subject' in event && event.subject ? (
                <p className="font-medium">{event.subject}</p>
              ) : null}
            </div>
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
